import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { listIndustriesApi, updateIndustry, uploadUrl } from '../lib/api';
import { ArrowLeft, Save, Image as ImageIcon } from 'lucide-react';

export default function IndustriesEditor({ onOpenMedia }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { setItems(await listIndustriesApi()); } catch {}
  };
  useEffect(() => { load(); }, []);

  if (editing) return <IndustryForm industry={editing} onBack={() => { setEditing(null); load(); }} onOpenMedia={onOpenMedia} />;

  return (
    <div className="bg-white border border-aqc-line rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl text-aqc-navy">Industries</h2>
        <span className="text-xs text-aqc-muted">{items.length} sectors</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((s) => (
          <button key={s.id} onClick={() => setEditing(s)} className="text-left flex gap-4 bg-aqc-cream/60 hover:bg-aqc-cream border border-aqc-line rounded-xl p-4 transition-colors">
            <img src={s.image} alt={s.title} className="w-24 h-16 object-cover rounded-md" />
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg text-aqc-navy">{s.title}</div>
              <div className="text-xs text-aqc-muted mt-0.5 line-clamp-2">{s.short}</div>
              <div className="mt-1.5 text-[0.7rem] uppercase tracking-[0.14em] text-aqc-red font-semibold">Edit →</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function IndustryForm({ industry, onBack, onOpenMedia }) {
  const [f, setF] = useState({
    title: industry.title || '',
    icon: industry.icon || '',
    short: industry.short || '',
    long: industry.long || '',
    image: industry.image || '',
    bullets: (industry.bullets || []).join('\n'),
    tags: (industry.tags || []).join(', '),
    order: industry.order || 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateIndustry(industry.id, {
        title: f.title,
        icon: f.icon,
        short: f.short,
        long: f.long,
        image: f.image,
        bullets: f.bullets.split('\n').map((x) => x.trim()).filter(Boolean),
        tags: f.tags.split(',').map((x) => x.trim()).filter(Boolean),
        order: Number(f.order) || 0,
      });
      toast({ title: 'Industry saved' });
      onBack();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again.' });
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-aqc-line rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-aqc-navy"><ArrowLeft size={14} /> Back to industries</button>
        <span className="text-xs text-aqc-muted font-mono">#{industry.id}</span>
      </div>
      <h2 className="font-serif text-2xl text-aqc-navy">Edit — {industry.title}</h2>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1.5" /></div>
        <div><Label>Icon (lucide name)</Label><Input value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} className="mt-1.5" placeholder="Wheat | Shirt | HardHat | HeartPulse | PawPrint | Cpu | Factory | Fuel" /></div>
        <div className="sm:col-span-2"><Label>Short summary (1 line)</Label><Input value={f.short} onChange={(e) => setF({ ...f, short: e.target.value })} className="mt-1.5" /></div>
        <div className="sm:col-span-2"><Label>Long description</Label><Textarea rows={4} value={f.long} onChange={(e) => setF({ ...f, long: e.target.value })} className="mt-1.5" /></div>
        <div className="sm:col-span-2">
          <Label>Image URL</Label>
          <div className="flex gap-2 mt-1.5">
            <Input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} />
            <Button type="button" variant="outline" onClick={() => onOpenMedia && onOpenMedia((url) => setF((s) => ({ ...s, image: url })))} className="whitespace-nowrap gap-1.5"><ImageIcon size={14} /> Pick</Button>
          </div>
          {f.image && <img src={uploadUrl(f.image)} alt="preview" className="mt-3 w-48 h-28 object-cover rounded-md border border-aqc-line" />}
        </div>
        <div><Label>Order</Label><Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} className="mt-1.5" /></div>
        <div><Label>Tags (comma-separated)</Label><Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} className="mt-1.5" /></div>
        <div className="sm:col-span-2">
          <Label>Bullets (one per line)</Label>
          <Textarea rows={5} value={f.bullets} onChange={(e) => setF({ ...f, bullets: e.target.value })} className="mt-1.5" />
        </div>
      </div>
      <div className="mt-6 flex gap-2">
        <Button onClick={save} disabled={saving} className="bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white gap-1.5"><Save size={14} /> {saving ? 'Saving…' : 'Save changes'}</Button>
        <Button variant="outline" onClick={onBack}>Cancel</Button>
      </div>
    </div>
  );
}
